import { BookingPaymentSummary } from '@components/Booking/BookingPaymentSummary';
import { BookingStatusChip } from '@components/Booking/BookingStatusChip';
import { InitiatePaymentSessionButton } from '@components/Booking/InitiatePaymentSessionButton';
import { ServiceCenterLocation } from '@components/ServiceCenter/ServiceCenterLocation';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingLocationType, BookingRecordDetailsCustomer } from '@navio/nsdk';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiInfoBanner, PrettySlug } from 'noddi-ui-web';
import {
  CustomDateFormats,
  NoddiDateFormats,
  noddiFormatDate,
  stringToUpperCase,
  useReservationCountdown
} from 'noddi-util';
import { getLongAddressName } from 'noddi-web';
import { RefObject } from 'react';
import { useServiceCenterLocation } from '../../hooks/useServiceCenterLocation';

interface Props {
  booking: BookingRecordDetailsCustomer;
  paymentButtonRef?: RefObject<HTMLDivElement | null>;
}

export const DraftPayBeforeContent = ({ booking, paymentButtonRef }: Props) => {
  const deliveryDate = stringToUpperCase(noddiFormatDate(booking.date, CustomDateFormats.FULL_DAY_MONTH));
  const deliveryTimeStartsAt = noddiFormatDate(booking.deliveryWindowStartsAt, NoddiDateFormats.TIME);
  const deliveryTimeEndsAt = noddiFormatDate(booking.deliveryWindowEndsAt, NoddiDateFormats.TIME);
  const deliveryTime = `${deliveryTimeStartsAt} - ${deliveryTimeEndsAt}`;
  // Staff-created bookings carry a deadline; a customer's own checkout may not.
  // Same one-line treatment as the garage order summary's reserved slot.
  const countdownDisplay = useReservationCountdown(booking.paymentDueAt);
  const isStationary = booking.locationType === BookingLocationType.STATIONARY;
  const { name: serviceCenterName, address: serviceCenterAddress } = useServiceCenterLocation({
    garageId: booking.garageId,
    serviceOrganizationId: booking.serviceOrganizationId,
    fallbackName: booking.serviceDepartmentBrand.name,
    fallbackAddress: booking.address,
    enabled: isStationary
  });

  return (
    <div className='flex flex-col gap-4 pb-24 md:pb-0'>
      <NoddiInfoBanner
        variant='warning'
        title={t`Payment required`}
        texts={[
          { value: t`Your booking is not yet confirmed. Please complete payment to confirm your booking.` },
          {
            value: t`If you just paid, it may take a few seconds for the payment to be processed. Try refreshing the page after a short while.`
          }
        ]}
      />

      <NoddiBasicCard className='p-0'>
        <div className='flex flex-row items-center justify-between gap-4 border-b border-grey/30 px-3 py-4 md:px-5'>
          <p className='text-lg'>
            <Trans>Booking status</Trans>
          </p>
          <BookingStatusChip booking={booking} />
        </div>

        <div className='flex flex-col gap-5 px-2 py-4 md:px-5'>
          <div className='flex flex-col gap-2'>
            <div className='flex items-center gap-3'>
              <NoddiIcon name='Hash' />
              <p>
                <Trans>Booking reference</Trans>: <PrettySlug slug={booking.slug} />
              </p>
            </div>
            <div className='flex items-center gap-3'>
              <NoddiIcon name='Calendar' />
              <p>{deliveryDate}</p>
            </div>
            <div className='flex items-center gap-3'>
              <NoddiIcon name='Clock' />
              <p>{deliveryTime}</p>
            </div>
            {countdownDisplay && (
              <p className='text-sm text-muted-foreground'>
                <Trans>Booking slot reserved</Trans> ({countdownDisplay})
              </p>
            )}
            {isStationary && serviceCenterAddress ? (
              <ServiceCenterLocation name={serviceCenterName} address={serviceCenterAddress} />
            ) : (
              <div className='flex items-center gap-3'>
                <NoddiIcon name='MapPin' />
                <p>{getLongAddressName(booking.address)}</p>
              </div>
            )}
          </div>

          <BookingPaymentSummary booking={booking} />
        </div>
      </NoddiBasicCard>

      <div className='fixed inset-x-0 bottom-5 z-docked px-4 md:static md:flex md:justify-end md:px-0'>
        <div className='mx-auto max-w-lg md:mx-0'>
          <InitiatePaymentSessionButton
            booking={booking}
            className='w-full md:w-fit'
            label={<Trans>Pay to confirm booking</Trans>}
            ref={paymentButtonRef}
          />
        </div>
      </div>
    </div>
  );
};
